import React, { useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { CollectionFormModal } from '../CollectionFormModal';
import { CollectionFormSheet } from '../CollectionFormSheet';
import { EmptyCollectionList } from '../EmptyCollectionList';
import {
  CollectionCard,
  CardPlaceholder,
  CollectionCardAdd,
} from '../CollectionCard';
import * as S from './CollectionsGrid.styled';
import { FLOATING_THRESHOLD } from './CollectionsGrid.constants';
import {
  GET_COLLECTIONS_LIST,
  GetCollectionsListQuery,
} from '@/features/BookmarkCollections/api';
import { useToastStore } from '@/store/useToastStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { PlatformSource } from '@/api/types/common.types';
import { mergeCollectionsList } from '@/utils/helpers';
import { COLLECTIONS_CHUNK_SIZE } from '@/constants/common';
import { UniversalPlusIcon } from '@/components/UI/Icons/Icons';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { useIosNudgeScrollOffTop } from '@/hooks/useIosNudgeScrollOffTop';

export const CollectionsGrid = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const openToast = useToastStore(state => state.openToast);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const iosNudgeScrollOffTop = useIosNudgeScrollOffTop();

  const { data, loading, refetch, fetchMore } =
    useQuery<GetCollectionsListQuery>(GET_COLLECTIONS_LIST, {
      fetchPolicy: 'network-only',
      variables: {
        first: COLLECTIONS_CHUNK_SIZE,
      },
    });

  const common = data?.currentUser?.bookmarkCollections;
  const collections = common?.nodes ?? [];
  const { endCursor, hasNextPage } = common?.pageInfo ?? {};
  const platform = isDesktop ? PlatformSource.Desktop : PlatformSource.Mobile;
  const hasCollections = collections.length > 0;
  const showFloatingButton = collections.length > FLOATING_THRESHOLD[platform];

  const handleCreate = useCallback(() => {
    if (isDesktop) {
      setIsCreateModalOpen(true);
      return;
    }

    iosNudgeScrollOffTop();
    setIsCreateSheetOpen(true);
  }, [isDesktop, iosNudgeScrollOffTop]);

  const handleCreateSuccess = useCallback(() => {
    setIsCreateSheetOpen(false);
    setIsCreateModalOpen(false);
    refetch();
  }, [refetch]);

  const handleActionSuccess = useCallback((message: string) => {
    openToast({ text: message });
  }, []);

  const handleLoadMore = () => {
    if (loading || !hasNextPage || !collections.length) return;

    fetchMore({
      variables: { after: endCursor },
      updateQuery: (prev, { fetchMoreResult }) =>
        mergeCollectionsList(prev, fetchMoreResult),
    });
  };

  const Loader = useMemo(() => {
    return Array.from({ length: 6 }).map((_, index) => (
      <CardPlaceholder key={index}>
        <SkeletonLoader height="100%" borderRadius={14} />
        <SkeletonLoader height={35} borderRadius={7} />
      </CardPlaceholder>
    ));
  }, []);

  const CreateCard = useMemo(() => {
    if (loading || !hasCollections) return;

    return <CollectionCardAdd onCreate={handleCreate} />;
  }, [loading, collections, isDesktop]);

  const FloatingCreateButton = useMemo(() => {
    if (loading || !hasCollections) return;

    return (
      <S.FloatingAddButton>
        <S.AddButton onClick={handleCreate}>
          <UniversalPlusIcon
            width={isDesktop ? 16 : 13}
            height={isDesktop ? 16 : 13}
            viewBox="0 0 15 14"
            fill={theme.palette.system.white}
          />
          {t('bookmark_collections.add_collection_empty_button')}
        </S.AddButton>
      </S.FloatingAddButton>
    );
  }, [loading, collections, isDesktop]);

  return (
    <>
      <S.CollectionsGrid data-testid="collections-grid">
        {loading
          ? Loader
          : collections.map(collection => (
              <CollectionCard
                key={collection.id}
                collection={collection}
                onUpdate={refetch}
                onActionSuccess={handleActionSuccess}
              />
            ))}

        {hasCollections && hasNextPage && (
          <InfiniteLoader onVisit={handleLoadMore} />
        )}

        {showFloatingButton ? null : CreateCard}
      </S.CollectionsGrid>

      {showFloatingButton ? FloatingCreateButton : null}

      {isDesktop ? (
        <CollectionFormModal
          hasDescription={false}
          isOpen={isCreateModalOpen}
          onSuccess={handleCreateSuccess}
          onClose={() => setIsCreateModalOpen(false)}
        />
      ) : (
        <CollectionFormSheet
          hasDescription={false}
          isOpen={isCreateSheetOpen}
          onSuccess={handleCreateSuccess}
          onClose={() => setIsCreateSheetOpen(false)}
        />
      )}

      {!loading && !hasCollections && (
        <EmptyCollectionList onCreate={handleCreate} />
      )}
    </>
  );
};
